// Libs
import { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Utils
// Services
import ApiServiceV2 from 'services/apiService/apiServiceV2';
// Selectors
import { selectSelectedSubModuleId } from 'components/Modules/selectors';
import {
	selectSelectedProjectId,
	selectSelectedOrderTargetFilterId,
} from 'core/selectors';
// Hooks
import useToastMessage from './useToastMessage';
import {
	getElementViewPayload,
	shouldShowFormSuccessToast,
} from './useForm.utils';
import { ElementViewBuilderContext } from 'core/context';
import { ElementViewBuilderConfig } from 'core';
import {
	resetActionForm,
	mutate1Object as mutate1ObjectInCore,
} from 'core/actions';
const initialValuesState = {};
const initialFormState = {
	isFetching: true,
	sending: false,
	title: undefined,
	components: [],
};

const useForm = ({ isOpen, formId, payload = {} }) => {
	const { reportId } = payload;
	// Select from datasource
	const dispatch = useDispatch();
	const sendToast = useToastMessage();
	const selectedModuleItemId = useSelector(selectSelectedSubModuleId);
	const selectedProjectId = useSelector(selectSelectedProjectId);
	const selectedOrderTargetFilterId = useSelector(
		selectSelectedOrderTargetFilterId,
	);
	const [form, setForm] = useState(initialFormState);
	const [loadedFormKey, setLoadedFormKey] = useState(null);
	const formDefinitionKey = JSON.stringify([formId, reportId]);
	const isFormFetching = form.isFetching || loadedFormKey !== formDefinitionKey;
	const [values, setValues] = useState(initialValuesState);
	const check = !!formId;
	const manualOrderTemplateSelection =
		form.key === 'createOrder' &&
		form.components.some(
			component => component.param === 'createOrderTemplateId',
		);
	const [templateSelection, setTemplateSelection] = useState(null);
	const scopeKey = JSON.stringify([
		isOpen,
		formId,
		reportId,
		selectedModuleItemId,
		selectedProjectId,
		selectedOrderTargetFilterId,
	]);
	const requestContext = useRef({ key: scopeKey, generation: 0 });
	if (requestContext.current.key !== scopeKey) {
		requestContext.current = {
			key: scopeKey,
			generation: requestContext.current.generation + 1,
		};
	}
	const context = requestContext.current;
	const selectionContextKey = `${scopeKey}:${context.generation}`;
	const componentRequests = useRef({});
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const onTemplateSelectionStateChange = useCallback(
		state => {
			if (mounted.current && requestContext.current === context) {
				setTemplateSelection({ ...state, context });
			}
		},
		[context],
	);
	const templateSelectionBlocked =
		manualOrderTemplateSelection &&
		(templateSelection?.context !== context ||
			templateSelection?.status !== 'ready' ||
			!templateSelection.options.some(
				option => String(option.id) === String(values.createOrderTemplateId),
			));

	const templateSelectionHidden =
		manualOrderTemplateSelection &&
		isOpen &&
		!isFormFetching &&
		!templateSelectionBlocked &&
		templateSelection.options.length === 1;

	const { setElementView } = useContext(ElementViewBuilderContext);
	const elementViewKeys =
		ElementViewBuilderConfig.getAuthorizedElementViewKeys();

	const handleViewElemente = responseViewElement => {
		const elementViewPayload = getElementViewPayload(
			responseViewElement,
			elementViewKeys,
		);

		if (!elementViewPayload) return;

		setElementView(elementViewPayload);
	};

	useEffect(() => {
		if (!isOpen) {
			setValues(initialValuesState);
			dispatch(resetActionForm());
		} else {
			setValues(prev => ({
				...prev,
				...(manualOrderTemplateSelection
					? { createOrderTemplateId: undefined }
					: {}),
				selectedModuleItemId,
				selectedProjectId,
				selectedOrderTargetFilterId,
			}));
		}
	}, [
		isOpen,
		selectedModuleItemId,
		selectedProjectId,
		selectedOrderTargetFilterId,
		formId,
		reportId,
		manualOrderTemplateSelection,
	]);

	// Get form details
	useEffect(() => {
		let isMounted = true;

		(async () => {
			if (!check) return;

			const data = await ApiServiceV2.get({
				url: `/feedManager/v1/form/${JSON.stringify({ formId, reportId })}`,
			});
			if (!isMounted) return;

			setForm(prev => ({
				...prev,
				...data,
				isFetching: false,
			}));
			setLoadedFormKey(formDefinitionKey);
		})();

		return () => {
			isMounted = false;
		};
	}, [formId, reportId]);

	const handleOnSubmitSuccess = () => {
		setValues(initialValuesState);
		setForm(prev => ({ ...prev, sending: false }));
		dispatch(resetActionForm());
		dispatch(mutate1ObjectInCore('datasource', { reload: true }));
	};

	const shouldShowSuccessToast = shouldShowFormSuccessToast(form.key);

	// Send form
	const submitForm = useCallback(() => {
		if (
			!check ||
			!isOpen ||
			isFormFetching ||
			form.sending ||
			templateSelectionBlocked
		)
			return;

		setForm(prev => ({
			...prev,
			sending: true,
		}));
		ApiServiceV2.post({
			url: `/feedManager/v1/form/submit/${JSON.stringify({
				formId,
				reportId,
			})}`,

			data: { values },
		})
			.then(responseViewElement => {
				handleViewElemente(responseViewElement);

				handleOnSubmitSuccess();

				if (shouldShowSuccessToast) {
					sendToast({
						message: 'Proceso completado',
						type: 'success',
					});
				}
			})
			.catch(err => {
				setForm(prev => ({
					...prev,
					sending: false,
				}));
				sendToast({
					message: err.message,
					type: 'error',
				});
			});
	}, [
		formId,
		reportId,
		values,
		shouldShowSuccessToast,
		isOpen,
		isFormFetching,
		form.sending,
		templateSelectionBlocked,
	]);

	// Get processed component sql data
	const getProcessedComponentSqlData = useCallback(
		async ({ componentId, searchValue, paramValues, returnKey }) => {
			if (
				!check ||
				!isOpen ||
				!mounted.current ||
				requestContext.current !== context
			)
				return null;
			const requestId = (componentRequests.current[componentId] || 0) + 1;
			componentRequests.current[componentId] = requestId;
			return ApiServiceV2.post({
				url: `/feedManager/v1/form/component/${JSON.stringify({
					formId,
					reportId,
					componentId,
				})}/processedSqlData`,
				data: {
					selectedProjectId,
					selectedOrderTargetFilterId,
					selectedModuleItemId,
					userParams: {
						...paramValues,
						searchValue,
					},
				},
			})
				.then(data => {
					if (
						!mounted.current ||
						requestContext.current !== context ||
						componentRequests.current[componentId] !== requestId
					)
						return null;
					const returnData = data[returnKey];
					const extraValues = { ...data };
					delete extraValues[returnKey];
					if (manualOrderTemplateSelection) {
						// SQL-derived defaults must not override user scope or the validated picker.
						delete extraValues.createOrderTemplateId;
						delete extraValues.selectedModuleItemId;
						delete extraValues.selectedProjectId;
						delete extraValues.selectedOrderTargetFilterId;
					}
					setValues(prev => ({ ...prev, ...extraValues }));
					return returnData;
				})
				.catch(() => null);
		},
		[
			formId,
			reportId,
			check,
			isOpen,
			context,
			manualOrderTemplateSelection,
			selectedModuleItemId,
			selectedProjectId,
			selectedOrderTargetFilterId,
		],
	);

	return {
		form: { ...form, isFetching: isFormFetching },
		values,
		setValues,
		submitForm,
		getProcessedComponentSqlData,
		manualOrderTemplateSelection,
		selectionContextKey,
		onTemplateSelectionStateChange,
		templateSelectionBlocked,
		templateSelectionHidden,
	};
};

export default useForm;
